import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Store, Download, Star, Zap, BarChart3, Puzzle, Brain,
  Leaf, FileOutput, Briefcase, X, CheckCircle2, Ban,
  ExternalLink, Tag, User, Calendar,
} from 'lucide-react';
import { RequireCapability } from '../../../components/auth/RequireCapability';
import { AdminConsoleLayout } from '../components/AdminConsoleLayout';
import { ModulePageHeader, ModuleLoadingState } from '../../shared/components/ModulePageHeader';
import { EmptyState } from '../../shared/components/EmptyState';
import { ErrorState } from '../../shared/components/ErrorState';
import { EnterpriseStatusBadge } from '../../shared/components/EnterpriseStatusBadge';
import {
  adminGetAllApps,
  adminSetAppStatus,
  adminCreateOfficialApp,
  getMarketplaceStats,
} from '../../../services/platformService';
import type { MarketplaceApp, AppCategory } from '../../../types/platformTypes';

const CATEGORIES: { key: AppCategory | 'all'; label: string; icon: React.ComponentType<{ size?: number }> }[] = [
  { key: 'all',          label: 'Todos',        icon: Store      },
  { key: 'Automation',   label: 'Automação',    icon: Zap        },
  { key: 'Analytics',    label: 'Analytics',    icon: BarChart3  },
  { key: 'Integrations', label: 'Integrações',  icon: Puzzle     },
  { key: 'Productivity', label: 'Produtividade',icon: Briefcase  },
  { key: 'Lean Tools',   label: 'Lean Tools',   icon: Leaf       },
  { key: 'Data Export',  label: 'Exportação',   icon: FileOutput },
  { key: 'AI',           label: 'AI',           icon: Brain      },
];

const PRICING_BADGE: Record<string, { label: string; tone: 'active' | 'trial' | 'past_due' }> = {
  free:         { label: 'FREE',     tone: 'active'   },
  freemium:     { label: 'FREEMIUM', tone: 'trial'    },
  paid:         { label: 'PAID',     tone: 'past_due' },
  subscription: { label: 'SUBSCR.',  tone: 'past_due' },
};

const STATUS_COLORS: Record<string, string> = {
  published: 'text-emerald-600 dark:text-emerald-400',
  draft:     'text-slate-500 dark:text-zinc-400',
  review:    'text-amber-600 dark:text-amber-400',
  suspended: 'text-red-500 dark:text-red-400',
};

// â”€â”€â”€ App Detail Modal â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

interface DetailModalProps {
  app: MarketplaceApp;
  onClose: () => void;
  onStatusChange: (appId: string, status: 'published' | 'suspended' | 'draft') => Promise<void>;
}

const AppDetailModal: React.FC<DetailModalProps> = ({ app, onClose, onStatusChange }) => {
  const [saving, setSaving] = useState(false);

  const handleStatus = async (status: 'published' | 'suspended' | 'draft') => {
    setSaving(true);
    await onStatusChange(app.id, status);
    setSaving(false);
    onClose();
  };

  const pricing = PRICING_BADGE[app.pricing_type] ?? { label: app.pricing_type.toUpperCase(), tone: 'active' as const };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="enterprise-card-surface w-full max-w-lg rounded shadow-xl border border-enterprise-border-soft mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-enterprise-border-soft shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-9 w-9 rounded bg-enterprise-border-soft flex items-center justify-center shrink-0">
              <Puzzle size={16} className="enterprise-text-secondary" />
            </div>
            <div className="min-w-0">
              <p className="text-[13px] font-semibold enterprise-text-primary truncate">{app.name}</p>
              <p className="text-[11px] enterprise-text-secondary">{app.developer_name}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="enterprise-text-secondary hover:enterprise-text-primary ml-2 shrink-0">
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4 overflow-y-auto">
          {/* Badges row */}
          <div className="flex items-center gap-2 flex-wrap">
            <EnterpriseStatusBadge label={pricing.label} tone={pricing.tone} />
            <span className={`text-[11px] font-semibold uppercase ${STATUS_COLORS[app.status]}`}>{app.status}</span>
            <span className="text-[11px] enterprise-text-secondary border border-enterprise-border-soft rounded px-1.5 py-0.5">{app.category}</span>
          </div>

          {/* Description */}
          <p className="text-[12px] enterprise-text-primary leading-relaxed">{app.description}</p>

          {/* Meta */}
          <div className="grid grid-cols-2 gap-2 text-[11px]">
            <div className="flex items-center gap-1.5 enterprise-text-secondary">
              <Star size={11} className="fill-amber-400 text-amber-400" />
              <span>{app.rating.toFixed(1)} ({app.rating_count} avaliações)</span>
            </div>
            <div className="flex items-center gap-1.5 enterprise-text-secondary">
              <Download size={11} />
              <span>{app.downloads.toLocaleString()} downloads</span>
            </div>
            <div className="flex items-center gap-1.5 enterprise-text-secondary">
              <User size={11} />
              <span>{app.developer_name}</span>
            </div>
            <div className="flex items-center gap-1.5 enterprise-text-secondary">
              <Calendar size={11} />
              <span>{new Date(app.created_at).toLocaleDateString('pt-BR')}</span>
            </div>
          </div>

          {/* Tags */}
          {app.tags.length > 0 && (
            <div className="flex items-start gap-2">
              <Tag size={11} className="enterprise-text-secondary mt-0.5 shrink-0" />
              <div className="flex flex-wrap gap-1">
                {app.tags.map((t) => (
                  <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-enterprise-border-soft enterprise-text-secondary">{t}</span>
                ))}
              </div>
            </div>
          )}

          {/* Slug */}
          <p className="text-[10px] font-mono enterprise-text-muted">slug: {app.slug}</p>
        </div>

        {/* Footer â€” Admin actions */}
        <div className="flex items-center justify-between gap-2 px-5 py-4 border-t border-enterprise-border-soft shrink-0">
          <div className="flex gap-2">
            {app.status !== 'published' && (
              <button
                type="button"
                onClick={() => handleStatus('published')}
                disabled={saving}
                className="inline-flex items-center gap-1.5 enterprise-btn-primary text-[11px] disabled:opacity-60"
              >
                <CheckCircle2 size={13} /> Publicar
              </button>
            )}
            {app.status === 'published' && (
              <button
                type="button"
                onClick={() => handleStatus('suspended')}
                disabled={saving}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium rounded border border-red-300 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-60"
              >
                <Ban size={13} /> Suspender
              </button>
            )}
            {app.status === 'suspended' && (
              <button
                type="button"
                onClick={() => handleStatus('draft')}
                disabled={saving}
                className="enterprise-btn-secondary text-[11px] disabled:opacity-60"
              >
                Mover para Rascunho
              </button>
            )}
          </div>
          {app.developer_url && (
            <a href={app.developer_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[11px] enterprise-text-secondary hover:text-enterprise-primary transition-colors">
              <ExternalLink size={11} /> Developer site
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

// â”€â”€â”€ Add App Modal â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

interface AddAppModalProps {
  onSave: (app: { name: string; slug: string; description: string; short_description: string; category: string; pricing_type: string; tags: string[] }) => Promise<void>;
  onClose: () => void;
}

const EMPTY_FORM = { name: '', slug: '', description: '', short_description: '', category: 'Lean Tools', pricing_type: 'free', tagsRaw: '' };

const AddAppModal: React.FC<AddAppModalProps> = ({ onSave, onClose }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (k: keyof typeof EMPTY_FORM) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm((p) => ({ ...p, [k]: e.target.value }));

  const autoSlug = (name: string) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const handleSave = async () => {
    if (!form.name.trim() || !form.slug.trim() || !form.description.trim()) {
      setError('Nome, slug e descrição são obrigatórios.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave({
        ...form,
        tags: form.tagsRaw.split(',').map((t) => t.trim()).filter(Boolean),
      });
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="enterprise-card-surface w-full max-w-lg rounded shadow-xl border border-enterprise-border-soft mx-4 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-enterprise-border-soft shrink-0">
          <span className="text-[13px] font-semibold enterprise-text-primary">Adicionar App Oficial</span>
          <button type="button" onClick={onClose} className="enterprise-text-secondary hover:enterprise-text-primary">
            <X size={16} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3 overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="enterprise-label">Nome *</label>
              <input value={form.name} onChange={(e) => { set('name')(e); setForm((p) => ({ ...p, slug: autoSlug(e.target.value) })); }} className="enterprise-input w-full text-[12px]" placeholder="VSM Analyzer" />
            </div>
            <div>
              <label className="enterprise-label">Slug *</label>
              <input value={form.slug} onChange={set('slug')} className="enterprise-input w-full text-[12px] font-mono" placeholder="vsm-analyzer" />
            </div>
          </div>

          <div>
            <label className="enterprise-label">Descrição curta</label>
            <input value={form.short_description} onChange={set('short_description')} className="enterprise-input w-full text-[12px]" placeholder="Uma linha resumida" />
          </div>

          <div>
            <label className="enterprise-label">Descrição completa *</label>
            <textarea value={form.description} onChange={set('description')} rows={3} className="enterprise-input w-full text-[12px] resize-none" placeholder="Descreva o app e suas funcionalidades" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="enterprise-label">Categoria</label>
              <select value={form.category} onChange={set('category')} className="enterprise-input w-full text-[12px]">
                {['Automation','Analytics','Integrations','Productivity','Lean Tools','Data Export','AI','Reporting'].map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="enterprise-label">Pricing</label>
              <select value={form.pricing_type} onChange={set('pricing_type')} className="enterprise-input w-full text-[12px]">
                <option value="free">Free</option>
                <option value="freemium">Freemium</option>
                <option value="paid">Paid</option>
                <option value="subscription">Subscription</option>
              </select>
            </div>
          </div>

          <div>
            <label className="enterprise-label">Tags (separadas por vírgula)</label>
            <input value={form.tagsRaw} onChange={set('tagsRaw')} className="enterprise-input w-full text-[12px]" placeholder="lean, vsm, analysis" />
          </div>

          {error && <p className="text-[11px] text-red-500">{error}</p>}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-enterprise-border-soft shrink-0">
          <button type="button" onClick={onClose} className="enterprise-btn-secondary text-[12px]">Cancelar</button>
          <button type="button" onClick={handleSave} disabled={saving} className="enterprise-btn-primary text-[12px] disabled:opacity-60">
            {saving ? 'Salvando...' : 'Publicar App'}
          </button>
        </div>
      </div>
    </div>
  );
};

// â”€â”€â”€ App Card â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const AppCard: React.FC<{ app: MarketplaceApp; onClick: () => void }> = ({ app, onClick }) => {
  const pricing = PRICING_BADGE[app.pricing_type] ?? { label: app.pricing_type.toUpperCase(), tone: 'active' as const };
  return (
    <button
      type="button"
      onClick={onClick}
      className="group text-left flex flex-col border border-enterprise-border-soft rounded p-4 hover:border-enterprise-primary transition-colors enterprise-card-surface w-full"
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded bg-enterprise-border-soft flex items-center justify-center shrink-0">
          <Puzzle size={20} className="enterprise-text-secondary" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-[12px] font-semibold enterprise-text-primary truncate">{app.name}</h3>
          <p className="text-[10px] enterprise-text-secondary">{app.developer_name}</p>
        </div>
        <EnterpriseStatusBadge label={pricing.label} tone={pricing.tone} />
      </div>

      <p className="text-[11px] enterprise-text-secondary line-clamp-2 mb-3 flex-1 leading-relaxed">
        {app.short_description || app.description}
      </p>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          <Star size={11} className="fill-amber-400 text-amber-400" />
          <span className="text-[11px] enterprise-text-secondary">{app.rating.toFixed(1)} ({app.rating_count})</span>
        </div>
        <div className="flex items-center gap-1 enterprise-text-secondary">
          <Download size={11} />
          <span className="text-[11px]">{app.downloads}</span>
        </div>
      </div>

      {app.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {app.tags.slice(0, 3).map((tag) => (
            <span key={tag} className="text-[10px] px-1.5 py-0.5 rounded bg-enterprise-border-soft enterprise-text-secondary">{tag}</span>
          ))}
        </div>
      )}

      {app.status !== 'published' && (
        <div className={`mt-2 text-[10px] font-semibold uppercase ${STATUS_COLORS[app.status]}`}>{app.status}</div>
      )}
    </button>
  );
};

// â”€â”€â”€ Main Page â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

interface AdminMarketplacePageProps {
  externalSearch?: string;
  externalCategory?: string;
  externalShowAdd?: boolean;
  onCloseAdd?: () => void;
}

export const AdminMarketplacePage: React.FC<AdminMarketplacePageProps> = ({ externalSearch, externalCategory, externalShowAdd, onCloseAdd }) => {
  const qc = useQueryClient();
  const search = externalSearch ?? '';
  const category = (externalCategory ?? 'all') as AppCategory | 'all';
  const [selected, setSelected] = useState<MarketplaceApp | null>(null);
  const [showAdd, setShowAdd] = useState(false);

  // Sync external showAdd trigger
  React.useEffect(() => {
    if (externalShowAdd) setShowAdd(true);
  }, [externalShowAdd]);

  const handleCloseAdd = () => {
    setShowAdd(false);
    onCloseAdd?.();
  };

  const appsQuery = useQuery({
    queryKey: ['admin-marketplace-apps'],
    queryFn: adminGetAllApps,
  });

  const statsQuery = useQuery({
    queryKey: ['marketplace-stats'],
    queryFn: getMarketplaceStats,
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'published' | 'suspended' | 'draft' }) =>
      adminSetAppStatus(id, status),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-marketplace-apps'] }),
  });

  const addMutation = useMutation({
    mutationFn: adminCreateOfficialApp,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-marketplace-apps'] });
      qc.invalidateQueries({ queryKey: ['marketplace-stats'] });
    },
  });

  const handleStatusChange = async (appId: string, status: 'published' | 'suspended' | 'draft') => {
    await statusMutation.mutateAsync({ id: appId, status });
  };

  const handleAddApp = async (app: Parameters<typeof adminCreateOfficialApp>[0]) => {
    const result = await addMutation.mutateAsync(app);
    if (!result) throw new Error('Falha ao criar app â€” verifique se o slug já existe.');
  };

  const apps = appsQuery.data ?? [];
  const filtered = apps.filter((a) => {
    const matchCat = category === 'all' || a.category === category;
    const q = search.toLowerCase();
    const matchSearch = !q || a.name.toLowerCase().includes(q) || a.description.toLowerCase().includes(q) || a.tags.some((t) => t.toLowerCase().includes(q));
    return matchCat && matchSearch;
  });

  const stats = statsQuery.data;

  return (
    <RequireCapability capability="admin.access" realRoleOnly>
      <AdminConsoleLayout>
        <div className="enterprise-page h-full overflow-y-auto pb-8">
          <ModulePageHeader
            icon={Store}
            title="App Marketplace"
            subtitle="Descubra e instale apps para estender o Workflow Manager"
          />

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 px-6 mb-5">
            {[
              { label: 'Apps Publicados',  value: stats?.total_apps ?? 0 },
              { label: 'Downloads',        value: (stats?.total_downloads ?? 0).toLocaleString() },
              { label: 'Developers',       value: stats?.active_developers ?? 0 },
              { label: 'Automações Ativas',value: stats?.active_automations ?? 0 },
            ].map(({ label, value }) => (
              <div key={label} className="enterprise-card-surface border border-enterprise-border-soft rounded p-4 text-center">
                <p className="text-2xl font-bold enterprise-text-primary">{value}</p>
                <p className="text-[11px] enterprise-text-secondary mt-0.5">{label}</p>
              </div>
            ))}
          </div>

          {/* Content */}
          <div className="px-6">
            {appsQuery.isPending && <ModuleLoadingState label="Carregando marketplace" />}
            {appsQuery.isError && <ErrorState message="Erro ao carregar apps." onRetry={appsQuery.refetch} />}

            {!appsQuery.isPending && !appsQuery.isError && filtered.length === 0 && (
              <EmptyState
                icon={Store}
                title="Nenhum app encontrado"
                description={search ? `Sem resultados para "${search}"` : 'Nenhum app cadastrado ainda.'}
                ctaLabel="Adicionar App"
                onCta={() => setShowAdd(true)}
              />
            )}

            {filtered.length > 0 && (
              <>
                <h2 className="text-[12px] font-semibold enterprise-text-primary mb-3">
                  {category === 'all' ? 'Todos os Apps' : category} ({filtered.length})
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {filtered.map((app) => (
                    <AppCard key={app.id} app={app} onClick={() => setSelected(app)} />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {selected && (
          <AppDetailModal
            app={selected}
            onClose={() => setSelected(null)}
            onStatusChange={handleStatusChange}
          />
        )}

        {showAdd && (
          <AddAppModal
            onSave={handleAddApp}
            onClose={handleCloseAdd}
          />
        )}
      </AdminConsoleLayout>
    </RequireCapability>
  );
};
